import React from 'react';
import { Calendar, TrendingUp, Percent, Clock, Database, Users, Code, Server, Settings } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета стоимости блокчейн-проекта
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Форматируем стоимость в доллары
  const formatUSD = (value) => `$${formatNumber(value, 0)}`;

  return (
    <ToolResult title="Результаты расчета стоимости проекта">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Общая стоимость проекта
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatUSD(results.totalCost)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Включает разработку и обслуживание на {results.maintenanceMonths} мес.
          </p>
        </div>

        {/* Сроки и команда */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Calendar className="w-4 h-4"/>
            Сроки реализации
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {results.duration} {formatMonthsLabel(results.duration)}
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Команда: {results.teamSize} специалистов ({results.totalManMonths} человеко-месяцев)
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div className="space-y-3">
          <ResultItem 
            label="Блокчейн-платформа" 
            value={results.platformName} 
            color="indigo" 
            icon={<Server className="w-4 h-4"/>}
          />
          
          <ResultItem 
            label="Тип проекта" 
            value={results.projectTypeName} 
            color="indigo" 
            icon={<Database className="w-4 h-4"/>}
          />
          
          <ResultItem 
            label="Язык смарт-контрактов" 
            value={results.language} 
            color="purple" 
            icon={<Code className="w-4 h-4"/>}
          />

          <ResultItem 
            label="Смарт-контракты" 
            value={`${results.smartContractsCount} шт.`} 
            icon={<Settings className="w-4 h-4"/>} 
          />

          <ResultItem 
            label="Расположение команды" 
            value={results.teamLocation} 
            icon={<Users className="w-4 h-4"/>} 
          />
        </div>

        <div className="space-y-3">
          <ResultItem 
            label="Стоимость разработки" 
            value={formatUSD(results.developmentCost)} 
            color="green" 
          />
          
          <ResultItem 
            label="Ежемесячные расходы" 
            value={formatUSD(results.monthlyDevelopmentCost)} 
            color="orange" 
          />
          
          <ResultItem 
            label="Стоимость поддержки" 
            value={formatUSD(results.maintenanceCost)} 
            color="blue" 
          />
          
          <ResultItem 
            label="Человеко-месяцы" 
            value={results.totalManMonths} 
            color="purple" 
          />
          
          <ResultItem 
            label="Средняя ставка специалиста" 
            value={formatUSD(results.avgMonthlyRatePerDeveloper)} 
            color="indigo" 
          />
        </div>
      </div>

      {/* Блок по составу затрат */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg mb-4">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Распределение затрат по компонентам</h4>
        
        <div className="space-y-3">
          {/* Прогресс-бары для основных компонентов затрат */}
          {renderCostProgressBar("Смарт-контракты", results.smartContractsCost + (results.tokenDevelopmentCost || 0), results.developmentCost, "bg-blue-500")}
          {renderCostProgressBar("Фронтенд", results.frontendCost, results.developmentCost, "bg-purple-500")}
          {renderCostProgressBar("Бэкенд", results.backendCost, results.developmentCost, "bg-green-500")}
          {renderCostProgressBar("Тестирование", results.testingCost, results.developmentCost, "bg-yellow-500")}
          {renderCostProgressBar("Безопасность", results.securityAuditCost, results.developmentCost, "bg-red-500")}
          {results.additionalFeaturesCost > 0 && 
            renderCostProgressBar("Доп. функции", results.additionalFeaturesCost, results.developmentCost, "bg-indigo-500")}
          {results.deploymentCost > 0 && 
            renderCostProgressBar("Деплоймент", results.deploymentCost, results.developmentCost, "bg-cyan-500")}
        </div>
      </div>

      {/* Сроки реализации */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Сроки реализации проекта</h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Оценка общей длительности проекта с учетом выбранной команды и сложности.
            </p>
          </div>
          <div className="flex items-center space-x-1 bg-blue-100 dark:bg-blue-800 px-3 py-2 rounded-lg">
            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <span className="font-bold text-blue-700 dark:text-blue-300 text-lg">
              {results.duration} {formatMonthsLabel(results.duration)}
            </span>
          </div>
        </div>
        <div className="mt-3 text-xs text-blue-700 dark:text-blue-300">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
            <div className="p-2 bg-blue-100/50 dark:bg-blue-800/50 rounded">
              <span className="font-medium">Планирование:</span> {Math.max(1, Math.round(results.duration * 0.15))} {formatMonthsLabel(Math.max(1, Math.round(results.duration * 0.15)))}
            </div>
            <div className="p-2 bg-blue-100/50 dark:bg-blue-800/50 rounded">
              <span className="font-medium">Разработка:</span> {Math.round(results.duration * 0.6)} {formatMonthsLabel(Math.round(results.duration * 0.6))}
            </div>
            <div className="p-2 bg-blue-100/50 dark:bg-blue-800/50 rounded">
              <span className="font-medium">Тестирование/деплоймент:</span> {Math.max(1, Math.round(results.duration * 0.25))} {formatMonthsLabel(Math.max(1, Math.round(results.duration * 0.25)))}
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Функция для отображения прогресс-бара затрат
const renderCostProgressBar = (label, value, total, colorClass) => {
  if (!value || value <= 0) return null;
  
  const percentage = Math.min(100, Math.round((value / total) * 100));
  
  return (
    <div>
      <div className="flex justify-between mb-1 text-xs">
        <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-gray-600 dark:text-gray-400">${Math.round(value).toLocaleString()} ({percentage}%)</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
        <div 
          className={`${colorClass} h-2.5 rounded-full`} 
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слова "месяц" в зависимости от числа
const formatMonthsLabel = (months) => {
  const lastDigit = months % 10;
  const lastTwoDigits = months % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'месяцев';
  if (lastDigit === 1) return 'месяц';
  if (lastDigit >= 2 && lastDigit <= 4) return 'месяца';
  return 'месяцев';
};

export default ResultsOverview;